<template>
  <div class="modal-overlay" @click.self="$emit('close')" @mousedown.stop @mouseup.stop>
    <div class="modal-container bulk-import-modal" @click.stop>
      <div class="modal-header">
        <div class="modal-header-content">
          <h2>{{ $t('accountCodes.bulkImportTitle') }}</h2>
        </div>
        <button @click="$emit('close')" class="btn btn-icon">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <div class="modal-body">
        <!-- Instructions -->
        <div class="import-instructions">
          <div class="instruction-icon">
            <font-awesome-icon icon="fa-solid fa-lightbulb" />
          </div>
          <div class="instruction-text">
            <h4>{{ $t('accountCodes.bulkImportInstructions', 'How to use') }}:</h4>
            <ul>
              <li>{{ $t('accountCodes.bulkImportStep1', 'Copy data from Excel, CSV, or any text format') }}</li>
              <li>{{ $t('accountCodes.bulkImportStep2', 'Paste it in the textarea below') }}</li>
              <li>{{ $t('accountCodes.bulkImportStep3', 'AI will automatically parse and structure the data') }}</li>
              <li>{{ $t('accountCodes.bulkImportStep4', 'Review and import the parsed codes') }}</li>
            </ul>
          </div>
        </div>

        <!-- Code Input (always visible) -->
        <div class="form-group">
          <label class="form-label">
            {{ $t('accountCodes.accountCodeOverride', 'Account code to apply') }}
            <span class="required">*</span>
          </label>
          <input
            v-model="overrideCodeValue"
            type="text"
            class="form-input"
            :placeholder="$t('accountCodes.accountCodePlaceholder', 'e.g., S, 401, etc.')"
            :disabled="parsing || importing"
            maxlength="50"
          />
          <p class="form-hint">
            {{ $t('accountCodes.overrideCodeHint', 'This code will be applied to all imported items') }}
          </p>
        </div>

        <!-- Description Name Input (always visible) -->
        <div class="form-group">
          <label class="form-label">
            {{ $t('accountCodes.descriptionName', 'Description Name') }}
          </label>
          <input
            v-model="descriptionName"
            type="text"
            class="form-input"
            :placeholder="$t('accountCodes.descriptionNamePlaceholder', 'e.g., Office Supplies, Stocks, Products')"
            :disabled="parsing || importing"
            maxlength="255"
          />
          <p class="form-hint">
            {{ $t('accountCodes.descriptionNameHint', 'A friendly name to identify this account code') }}
          </p>
          <div v-if="mappingType !== 'none'" class="info-message">
            <font-awesome-icon icon="fa-solid fa-circle-info" />
            <span>You can add {{ mappingType === 'barcode' ? 'barcodes' : 'descriptions' }} to existing account codes. Each entry will be saved separately.</span>
          </div>
        </div>

        <!-- Import Method Selection -->
        <div class="form-group">
          <label class="form-label">
            {{ $t('accountCodes.importMethod', 'Import Method') }}
          </label>
          <div class="radio-group radio-group-horizontal">
            <label class="radio-label">
              <input
                type="radio"
                v-model="importMethod"
                value="paste"
                :disabled="parsing || importing"
              />
              <span>📋 {{ $t('accountCodes.pasteText', 'Paste Text') }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                v-model="importMethod"
                value="excel"
                :disabled="parsing || importing"
              />
              <span>📊 {{ $t('accountCodes.uploadExcel', 'Upload Excel') }}</span>
            </label>
          </div>
        </div>

        <!-- Mapping Type Selection -->
        <div class="form-group">
          <label class="form-label">
            {{ $t('accountCodes.mappingType', 'Import data as') }}
          </label>
          <div class="radio-group">
            <label class="radio-label">
              <input
                type="radio"
                v-model="mappingType"
                value="none"
                :disabled="parsing || importing"
              />
              <span>{{ $t('accountCodes.mappingTypeNone', 'Account Codes Only') }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                v-model="mappingType"
                value="barcode"
                :disabled="parsing || importing"
              />
              <span>{{ $t('accountCodes.mappingTypeBarcode', 'Barcode Mapping') }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                v-model="mappingType"
                value="description"
                :disabled="parsing || importing"
              />
              <span>{{ $t('accountCodes.mappingTypeDescription', 'Description Mapping') }}</span>
            </label>
          </div>
          <p class="form-hint">
            <span v-if="mappingType === 'none'">{{ $t('accountCodes.mappingTypeNoneHint', 'Import only account code, name, and type') }}</span>
            <span v-else-if="mappingType === 'barcode'">{{ $t('accountCodes.mappingTypeBarcodeHint', 'Use first column as barcode mappings (e.g., 0001, 0002)') }}</span>
            <span v-else-if="mappingType === 'description'">{{ $t('accountCodes.mappingTypeDescriptionHint', 'Use first column as description keywords (e.g., Diesel, Fuel)') }}</span>
          </p>
        </div>

        <!-- Select Existing Account Code (shown for barcode/description mapping) -->
        <div v-if="mappingType !== 'none'" class="form-group">
          <label class="form-label">
            {{ $t('accountCodes.selectExistingCode', 'Add to existing account code') }}
          </label>
          <select
            v-model="selectedExistingCode"
            @change="selectExistingCode"
            class="form-input"
            :disabled="parsing || importing"
          >
            <option value="">{{ $t('accountCodes.createNew', 'Create new account code') }}</option>
            <option v-for="code in existingAccountCodes" :key="code.id" :value="code.id">
              {{ code.account_code }} - {{ code.account_name || 'No name' }}
            </option>
          </select>
          <p class="form-hint">
            {{ $t('accountCodes.selectExistingHint', 'Select an existing account code to add new mappings to it') }}
          </p>
        </div>

        <!-- Excel File Upload (shown when import method is excel) -->
        <div v-if="importMethod === 'excel'" class="form-group">
          <label class="form-label">
            {{ $t('accountCodes.uploadExcelFile', 'Upload Excel File') }}
            <span class="required">*</span>
          </label>
          <div class="file-upload-area" :class="{ 'drag-over': isDragOver }"
               @drop.prevent="handleFileDrop"
               @dragover.prevent="isDragOver = true"
               @dragleave.prevent="isDragOver = false">
            <input
              ref="fileInput"
              type="file"
              accept=".xlsx,.xls"
              @change="handleFileSelect"
              :disabled="parsing || importing"
              style="display: none"
            />
            <div v-if="!excelFile" class="upload-prompt" @click="$refs.fileInput.click()">
              <font-awesome-icon icon="fa-solid fa-file-excel" class="upload-icon" />
              <p class="upload-text">{{ $t('accountCodes.dropOrClickExcel', 'Drop Excel file here or click to browse') }}</p>
              <p class="upload-hint">{{ $t('accountCodes.excelFormats', 'Supports .xlsx and .xls files') }}</p>
            </div>
            <div v-else class="file-selected">
              <font-awesome-icon icon="fa-solid fa-file-excel" class="file-icon" />
              <div class="file-info">
                <p class="file-name">{{ excelFile.name }}</p>
                <p class="file-size">{{ formatFileSize(excelFile.size) }}</p>
              </div>
              <button @click.stop="removeFile" class="btn-remove-file">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
        </div>

        <!-- Column Mapping (shown after Excel is loaded) -->
        <div v-if="importMethod === 'excel' && excelData.length > 0" class="form-group">
          <label class="form-label">
            {{ $t('accountCodes.mapColumns', 'Map Excel Columns') }}
          </label>
          <div class="column-mapping">
            <div class="mapping-row">
              <label>{{ $t('accountCodes.accountCodeColumn', 'Account Code Column') }} <span class="required">*</span></label>
              <select v-model="columnMapping.accountCode" class="form-input" :disabled="parsing || importing">
                <option value="">-- {{ $t('accountCodes.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('accountCodes.accountNameColumn', 'Account Name Column') }} <span class="required">*</span></label>
              <select v-model="columnMapping.accountName" class="form-input" :disabled="parsing || importing">
                <option value="">-- {{ $t('accountCodes.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('accountCodes.barcodeColumn', 'Barcode Mapping Column') }}</label>
              <select v-model="columnMapping.barcode" class="form-input" :disabled="parsing || importing">
                <option value="">-- {{ $t('accountCodes.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('accountCodes.descriptionColumn', 'Description Mapping Column') }}</label>
              <select v-model="columnMapping.description" class="form-input" :disabled="parsing || importing">
                <option value="">-- {{ $t('accountCodes.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('accountCodes.internalCodeColumn', 'Internal Code Column') }}</label>
              <select v-model="columnMapping.internalCode" class="form-input" :disabled="parsing || importing">
                <option value="">-- {{ $t('accountCodes.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
            <div class="mapping-row">
              <label>{{ $t('accountCodes.accountTypeColumn', 'Account Type Column') }}</label>
              <select v-model="columnMapping.accountType" class="form-input" :disabled="parsing || importing">
                <option value="">-- {{ $t('accountCodes.selectColumn', 'Select Column') }} --</option>
                <option v-for="(col, idx) in excelColumns" :key="idx" :value="idx">
                  ({{ getColumnLetter(idx) }}) {{ col }}
                </option>
              </select>
            </div>
          </div>
          
          <!-- Default Account Type for all rows if no column mapped -->
          <div v-if="columnMapping.accountType === ''" class="form-group">
            <label class="form-label">
              {{ $t('accountCodes.defaultAccountType', 'Default Account Type') }} <span class="required">*</span>
            </label>
            <select v-model="defaultAccountType" class="form-input" :disabled="parsing || importing">
              <option value="expense">{{ $t('modals.typeExpense') }}</option>
              <option value="revenue">{{ $t('modals.typeRevenue') }}</option>
              <option value="neutral">{{ $t('modals.typeNeutral') }}</option>
            </select>
          </div>
          
          <div class="column-mapping-help">
            <p><strong>{{ $t('accountCodes.columnGuide', 'Column Guide') }}:</strong></p>
            <ul>
              <li><strong>{{ $t('accountCodes.accountCode', 'Account Code') }}:</strong> {{ $t('accountCodes.accountCodeHelp', 'Required. The account code (e.g., S, 611, 701)') }}</li>
              <li><strong>{{ $t('accountCodes.accountName', 'Account Name') }}:</strong> {{ $t('accountCodes.accountNameHelp', 'Required. The name/description of the account') }}</li>
              <li><strong>{{ $t('accountCodes.barcodeMapping', 'Barcode Mapping') }}:</strong> {{ $t('accountCodes.barcodeMappingHelp', 'Optional. Comma-separated barcodes to map to this account (e.g., 0001, 0002)') }}</li>
              <li><strong>{{ $t('accountCodes.descriptionMapping', 'Description Mapping') }}:</strong> {{ $t('accountCodes.descriptionMappingHelp', 'Optional. Comma-separated keywords to map to this account (e.g., Diesel, Fuel)') }}</li>
              <li><strong>{{ $t('accountCodes.internalCode', 'Internal Code') }}:</strong> {{ $t('accountCodes.internalCodeHelp', 'Optional. Internal code for this account (e.g., IC-001, S,U,M,T)') }}</li>
            </ul>
          </div>

          <!-- Data Preview -->
          <div v-if="columnMapping.accountCode !== ''" class="data-preview">
            <h4>{{ $t('accountCodes.dataPreview', 'Data Preview') }} ({{ $t('accountCodes.first5Rows', 'First 5 rows') }})</h4>
            <div class="preview-table-wrapper">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th>{{ $t('accountCodes.accountCode', 'Account Code') }}</th>
                    <th v-if="columnMapping.accountName !== ''">{{ $t('accountCodes.accountName', 'Account Name') }}</th>
                    <th v-if="columnMapping.barcode !== ''">{{ $t('accountCodes.barcode', 'Barcode Mapping') }}</th>
                    <th v-if="columnMapping.description !== ''">{{ $t('accountCodes.description', 'Description Mapping') }}</th>
                    <th v-if="columnMapping.internalCode !== ''">{{ $t('accountCodes.internalCode', 'Internal Code') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in previewData" :key="idx">
                    <td class="code-cell">{{ row.accountCode }}</td>
                    <td v-if="columnMapping.accountName !== ''">{{ row.accountName }}</td>
                    <td v-if="columnMapping.barcode !== ''">{{ row.barcode }}</td>
                    <td v-if="columnMapping.description !== ''">{{ row.description }}</td>
                    <td v-if="columnMapping.internalCode !== ''">{{ row.internalCode }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Paste Area (shown when import method is paste) -->
        <div v-if="importMethod === 'paste'" class="form-group">
          <label class="form-label">
            {{ $t('accountCodes.pasteData', 'Paste your account codes data') }}
            <span class="required">*</span>
          </label>
          <textarea
            v-model="pastedText"
            :placeholder="getMappingPlaceholder()"
            class="form-textarea"
            rows="8"
            :disabled="parsing || importing"
          ></textarea>
          <p class="form-hint">
            <span v-if="mappingType === 'none'">{{ $t('accountCodes.pasteDataHint', 'Include columns: Code, Name, Type (expense/revenue/asset/liability), Category (optional)') }}</span>
            <span v-else-if="mappingType === 'barcode'">{{ $t('accountCodes.pasteDataHintBarcode', 'Include columns: Barcode(s), Account Code, Name, Type') }}</span>
            <span v-else-if="mappingType === 'description'">{{ $t('accountCodes.pasteDataHintDescription', 'Include columns: Keyword(s), Account Code, Name, Type') }}</span>
          </p>
        </div>

        <!-- Import Button -->
        <div class="action-buttons">
          <button
            @click="parseData"
            :disabled="!canImport || parsing"
            class="btn-primary btn-large"
          >
            <div v-if="parsing" class="btn-spinner"></div>
            <font-awesome-icon v-else icon="fa-solid fa-file-import" />
            {{ parsing ? $t('accountCodes.importing', 'Importing...') : $t('accountCodes.importCodes', 'Import Codes') }}
          </button>
        </div>

        <!-- Parsing Error -->
        <div v-if="parseError" class="error-message">
          <font-awesome-icon icon="fa-solid fa-triangle-exclamation" />
          {{ parseError }}
        </div>

        <!-- Import Results -->
        <div v-if="importResult" class="import-result" :class="importResult.type">
          <font-awesome-icon :icon="importResult.type === 'success' ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-exclamation'" />
          <div>
            <p class="result-message">{{ importResult.message }}</p>
            <ul v-if="importResult.errors && importResult.errors.length > 0" class="error-list">
              <li v-for="(error, index) in importResult.errors" :key="index">{{ error }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import * as XLSX from 'xlsx';
import api from '@/utils/api';

const props = defineProps({
  companyId: {
    type: [String, Number],
    required: true
  }
});

const emit = defineEmits(['close', 'imported']);

const importMethod = ref('paste'); // 'paste' or 'excel'
const pastedText = ref('');
const parsing = ref(false);
const parseError = ref(null);
const importResult = ref(null);
const overrideCodeValue = ref('');
const descriptionName = ref('');
const mappingType = ref('none'); // none, barcode, description
const appendToExisting = ref(false);
const existingAccountCodes = ref([]);
const selectedExistingCode = ref('');

// Excel-related refs
const fileInput = ref(null);
const excelFile = ref(null);
const excelData = ref([]);
const excelColumns = ref([]);
const isDragOver = ref(false);
const columnMapping = ref({
  accountCode: '',
  accountName: '',
  description: '',
  barcode: '',
  internalCode: '',
  accountType: ''
});
const defaultAccountType = ref('expense');

// Computed property to check if import can proceed
const canImport = computed(() => {
  if (importMethod.value === 'paste') {
    return pastedText.value.trim() !== '';
  } else if (importMethod.value === 'excel') {
    return excelData.value.length > 0 && 
           columnMapping.value.accountCode !== '' && 
           columnMapping.value.accountName !== '';
  }
  return false;
});

// Computed property for data preview from Excel
const previewData = computed(() => {
  if (excelData.value.length === 0) return [];
  
  const preview = excelData.value.slice(0, 5).map(row => {
    const result = {
      accountCode: columnMapping.value.accountCode !== '' ? row[columnMapping.value.accountCode] || '' : '',
      accountName: columnMapping.value.accountName !== '' ? row[columnMapping.value.accountName] || '' : '',
      description: columnMapping.value.description !== '' ? row[columnMapping.value.description] || '' : '',
      barcode: columnMapping.value.barcode !== '' ? row[columnMapping.value.barcode] || '' : '',
      internalCode: columnMapping.value.internalCode !== '' ? row[columnMapping.value.internalCode] || '' : ''
    };
    return result;
  });
  
  return preview;
});

const getMappingPlaceholder = () => {
  if (mappingType.value === 'barcode') {
    return 'Paste data with barcodes...\n\nExample:\n0001, 0002  S  Fuel Expense  expense\n0003  701  Sales Revenue  revenue';
  } else if (mappingType.value === 'description') {
    return 'Paste data with keywords...\n\nExample:\nDiesel, Fuel  S  Fuel Expense  expense\nSales, Revenue  701  Sales Revenue  revenue';
  } else {
    return 'Paste Excel data, table, or list here...\n\nExample:\n401 Sales Revenue Revenue\n501 Operating Expenses Expense\n601 Cash Asset';
  }
};

// Excel file handling
const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    processExcelFile(file);
  }
};

const handleFileDrop = (event) => {
  isDragOver.value = false;
  const file = event.dataTransfer.files[0];
  if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
    processExcelFile(file);
  } else {
    parseError.value = 'Please upload a valid Excel file (.xlsx or .xls)';
  }
};

const processExcelFile = (file) => {
  excelFile.value = file;
  parseError.value = null;
  
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Get first sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Convert to JSON (array of arrays)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      
      if (jsonData.length === 0) {
        parseError.value = 'Excel file is empty';
        return;
      }
      
      // First row as headers
      excelColumns.value = jsonData[0].map((col, idx) => col || `Column ${idx + 1}`);
      
      // Rest as data (skip header row)
      excelData.value = jsonData.slice(1).filter(row => row.some(cell => cell !== ''));
      
    } catch (error) {
      parseError.value = 'Failed to read Excel file: ' + error.message;
      console.error('Excel parsing error:', error);
    }
  };
  
  reader.readAsArrayBuffer(file);
};

const autoDetectColumns = () => {
  // Try to auto-detect columns based on common header names
  const headers = excelColumns.value.map(h => (h || '').toString().toLowerCase());
  
  // Account Code (column A or B would be index 0 or 1)
  const accountCodeIdx = headers.findIndex(h => 
    h.includes('account') && h.includes('code') || 
    h.includes('код') || 
    h === 'code' ||
    h === 'account'
  );
  if (accountCodeIdx !== -1) columnMapping.value.accountCode = accountCodeIdx;
  else columnMapping.value.accountCode = 0; // Default to column A (index 0)
  
  // Account Name (column B or C would be index 1 or 2)
  const accountNameIdx = headers.findIndex(h => 
    h.includes('account') && h.includes('name') || 
    h.includes('име') || 
    h === 'name' ||
    h.includes('наименование') ||
    h.includes('account_name')
  );
  if (accountNameIdx !== -1) columnMapping.value.accountName = accountNameIdx;
  else columnMapping.value.accountName = 1; // Default to column B (index 1)
  
  // Description Mapping (column C or D would be index 2 or 3)
  const descIdx = headers.findIndex(h => 
    h.includes('description') && h.includes('mapping') || 
    h.includes('keyword') ||
    h.includes('desc') && h.includes('map')
  );
  if (descIdx !== -1) columnMapping.value.description = descIdx;
  
  // Barcode Mapping (column D or E would be index 3 or 4)
  const barcodeIdx = headers.findIndex(h => 
    h.includes('barcode') && h.includes('mapping') || 
    h.includes('баркод') ||
    h.includes('код на артикул') ||
    h.includes('barcode_mapping')
  );
  if (barcodeIdx !== -1) columnMapping.value.barcode = barcodeIdx;
  
  // Internal Code
  const internalCodeIdx = headers.findIndex(h => 
    h.includes('internal') && h.includes('code') || 
    h.includes('вътрешен код') ||
    h.includes('internal_code') ||
    h === 'internal code'
  );
  if (internalCodeIdx !== -1) columnMapping.value.internalCode = internalCodeIdx;
};

const removeFile = () => {
  excelFile.value = null;
  excelData.value = [];
  excelColumns.value = [];
  columnMapping.value = {
    accountCode: '',
    accountName: '',
    description: '',
    barcode: '',
    internalCode: ''
  };
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const getColumnLetter = (index) => {
  let letter = '';
  let num = index;
  while (num >= 0) {
    letter = String.fromCharCode(65 + (num % 26)) + letter;
    num = Math.floor(num / 26) - 1;
  }
  return letter;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const parseData = async () => {
  let dataToImport = '';
  
  // Prepare data based on import method
  if (importMethod.value === 'paste') {
    if (!pastedText.value.trim()) return;
    dataToImport = pastedText.value;
  } else if (importMethod.value === 'excel') {
    if (excelData.value.length === 0 || columnMapping.value.accountCode === '') return;
    
    // Convert Excel data to structured JSON for backend
    const structuredData = excelData.value.map(row => {
      // Get account type from column or use default
      let accountType = defaultAccountType.value;
      if (columnMapping.value.accountType !== '' && row[columnMapping.value.accountType]) {
        const typeValue = String(row[columnMapping.value.accountType]).toLowerCase().trim();
        // Map various input formats to valid types
        if (typeValue.includes('revenue') || typeValue.includes('income') || typeValue.includes('приход')) {
          accountType = 'revenue';
        } else if (typeValue.includes('expense') || typeValue.includes('разход')) {
          accountType = 'expense';
        } else if (typeValue.includes('neutral') || typeValue.includes('неутрален')) {
          accountType = 'neutral';
        }
      }
      
      const item = {
        account_code: row[columnMapping.value.accountCode] || '',
        account_name: columnMapping.value.accountName !== '' ? (row[columnMapping.value.accountName] || '') : '',
        account_type: accountType,
        is_active: true
      };
      
      // Add barcode mapping if column is selected
      if (columnMapping.value.barcode !== '' && row[columnMapping.value.barcode]) {
        const barcode = String(row[columnMapping.value.barcode]).trim().replace(/,/g, '.');
        if (barcode) {
          item.barcode_mapping = [barcode];
        }
      }
      
      // Add description mapping if column is selected
      if (columnMapping.value.description !== '' && row[columnMapping.value.description]) {
        const description = String(row[columnMapping.value.description]).trim().replace(/,/g, '.');
        if (description) {
          item.description_mapping = [description];
        }
      }
      
      // Add internal code if column is selected
      if (columnMapping.value.internalCode !== '' && row[columnMapping.value.internalCode]) {
        const internalCode = String(row[columnMapping.value.internalCode]).trim();
        if (internalCode) {
          item.internal_code = internalCode;
          
          // Create internal code mappings
          if (item.barcode_mapping && item.barcode_mapping.length > 0) {
            item.barcode_internal_code_mapping = {};
            item.barcode_mapping.forEach(barcode => {
              item.barcode_internal_code_mapping[barcode] = internalCode;
            });
          }
          
          if (item.description_mapping && item.description_mapping.length > 0) {
            item.description_internal_code_mapping = {};
            item.description_mapping.forEach(desc => {
              item.description_internal_code_mapping[desc] = internalCode;
            });
          }
        }
      }
      
      return item;
    }).filter(item => item.account_code); // Remove items without account code
    
    // Convert to JSON string
    dataToImport = JSON.stringify(structuredData);
  }

  parsing.value = true;
  parseError.value = null;
  importResult.value = null;

  try {
    const payload = {
      pasted_text: dataToImport,
      mapping_type: mappingType.value,
      append_to_existing: appendToExisting.value,
    };
    
    // Add override code if provided
    if (overrideCodeValue.value.trim()) {
      payload.override_code = overrideCodeValue.value.trim();
    }
    
    // Add description name if provided
    if (descriptionName.value.trim()) {
      payload.account_name = descriptionName.value.trim();
    }

    const response = await api.post(`/api/companies/${props.companyId}/account-codes/bulk-import`, payload);

    const { imported, failed, errors } = response.data;

    // Show success message
    if (imported > 0) {
      // Use toast notification if available, otherwise show result
      if (window.$toast) {
        window.$toast.success(`Successfully imported ${imported} account code${imported !== 1 ? 's' : ''}`);
      }
      
      // Close modal and refresh
      emit('imported');
      emit('close');
    } else {
      parseError.value = 'No valid account codes could be parsed. Please check your data format.';
    }

    // Show errors if any
    if (failed > 0) {
      importResult.value = {
        type: 'warning',
        message: `Imported ${imported}, but ${failed} failed.`,
        errors: errors || []
      };
    }
  } catch (error) {
    parseError.value = error.response?.data?.message || 'Failed to parse and import account codes. Please try again.';
    importResult.value = {
      type: 'error',
      message: error.response?.data?.message || 'Failed to import account codes.',
      errors: error.response?.data?.errors || []
    };
    console.error('Import error:', error);
  } finally {
    parsing.value = false;
  }
};

// Load existing account codes
const loadExistingAccountCodes = async () => {
  try {
    const response = await api.get(`/api/companies/${props.companyId}/account-codes`);
    existingAccountCodes.value = response.data.data || response.data;
  } catch (error) {
    console.error('Failed to load account codes:', error);
  }
};

// When selecting an existing code, populate the fields
const selectExistingCode = (event) => {
  const codeId = event.target.value;
  if (codeId) {
    const code = existingAccountCodes.value.find(c => c.id == codeId);
    if (code) {
      overrideCodeValue.value = code.account_code;
      descriptionName.value = code.account_name || '';
      appendToExisting.value = true;
    }
  } else {
    appendToExisting.value = false;
  }
};

onMounted(() => {
  loadExistingAccountCodes();
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  overflow-y: auto;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  padding: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.required {
  color: #ef4444;
  margin-left: 4px;
}

.form-hint {
  margin-top: 6px;
  font-size: 13px;
  color: var(--text-secondary);
  min-height: 20px;
  line-height: 1.4;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--primary);
}

.checkbox-label input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-group-horizontal {
  flex-direction: row;
  gap: 12px;
}

.radio-group-horizontal .radio-label {
  flex: 1;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
  padding: 12px;
  border: 2px solid var(--border);
  border-radius: 8px;
  transition: all 0.2s ease;
  min-height: 46px;
}

.radio-label:hover {
  border-color: var(--primary);
  background-color: rgba(14, 165, 233, 0.05);
}

.radio-label input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--primary);
}

.radio-label input[type="radio"]:checked + span {
  font-weight: 600;
  color: var(--primary);
}

.radio-label input[type="radio"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  transition: all 0.2s ease;
  font-family: monospace;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.form-input:disabled {
  background-color: var(--bg-gray);
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  color: var(--text-primary);
  border: 2px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.btn-icon:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.bulk-import-modal {
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
}

.modal-body {
  padding: 24px;
}

.import-instructions {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #dbeafe 0%, #f0f9ff 100%);
  border-left: 4px solid var(--primary);
  border-radius: 8px;
  margin-bottom: 24px;
}

.instruction-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary);
  color: white;
  border-radius: 50%;
  font-size: 20px;
}

.instruction-text h4 {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.instruction-text ul {
  margin: 0;
  padding-left: 20px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.6;
}

.instruction-text li {
  margin-bottom: 4px;
}

.form-textarea {
  width: 100%;
  min-height: 280px;
  max-height: 280px;
  height: 280px;
  padding: 12px;
  border: 2px solid var(--border);
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  transition: all 0.2s ease;
}

.form-textarea:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.form-textarea:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

.btn-large {
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 600;
}

.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  display: inline-block;
  margin-right: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  border-radius: 8px;
  color: #dc2626;
  margin-top: 16px;
  font-size: 14px;
}

.info-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  color: #1e40af;
  margin-top: 12px;
  font-size: 14px;
}

.info-message svg {
  color: #3b82f6;
  font-size: 16px;
  flex-shrink: 0;
}

.parsed-results {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px dashed var(--border);
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.results-header h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.results-header h3 svg {
  color: #10b981;
}

.parsed-table-wrapper {
  max-height: 300px;
  overflow-y: auto;
  border: 2px solid var(--border);
  border-radius: 8px;
  margin-bottom: 20px;
}

.parsed-table {
  width: 100%;
  border-collapse: collapse;
}

.parsed-table thead {
  position: sticky;
  top: 0;
  background: var(--bg-gray);
  z-index: 1;
}

.parsed-table th {
  padding: 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--text-secondary);
  border-bottom: 2px solid var(--border);
}

.parsed-table td {
  padding: 12px;
  border-bottom: 1px solid var(--border);
  font-size: 14px;
}

.parsed-table tbody tr:hover {
  background: #f8fafc;
}

.code-cell {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  color: var(--primary);
}

.type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.type-expense {
  background: #fef3c7;
  color: #d97706;
}

.type-revenue {
  background: #d1fae5;
  color: #059669;
}

.type-asset {
  background: #dbeafe;
  color: #0284c7;
}

.type-liability {
  background: #fecaca;
  color: #dc2626;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.status-badge.active {
  background: #d1fae5;
  color: #059669;
}

.import-result {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 14px;
}

.import-result.success {
  background: #d1fae5;
  border-left: 4px solid #10b981;
  color: #065f46;
}

.import-result.warning {
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  color: #92400e;
}

.import-result.error {
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  color: #dc2626;
}

.import-result svg {
  flex-shrink: 0;
  font-size: 20px;
  margin-top: 2px;
}

.result-message {
  font-weight: 600;
  margin: 0 0 8px 0;
}

.error-list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.6;
}

.error-list li {
  margin-bottom: 4px;
}

/* Excel Upload Styles */
.file-upload-area {
  border: 2px dashed var(--border);
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  transition: all 0.3s ease;
  background: var(--bg-gray);
  cursor: pointer;
}

.file-upload-area:hover {
  border-color: var(--primary);
  background: rgba(14, 165, 233, 0.05);
}

.file-upload-area.drag-over {
  border-color: var(--primary);
  background: rgba(14, 165, 233, 0.1);
  transform: scale(1.02);
}

.upload-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-icon {
  font-size: 48px;
  color: #10b981;
  margin-bottom: 8px;
}

.upload-text {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.upload-hint {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.file-selected {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  text-align: left;
}

.file-icon {
  font-size: 36px;
  color: #10b981;
  flex-shrink: 0;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
  word-break: break-word;
}

.file-size {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.btn-remove-file {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
  border: none;
  border-radius: 6px;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-remove-file:hover {
  background: #fee2e2;
  transform: scale(1.1);
}

/* Column Mapping Styles */
.column-mapping {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: var(--bg-gray);
  border-radius: 10px;
  margin-bottom: 20px;
}

.mapping-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mapping-row label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

/* Data Preview Styles */
.data-preview {
  margin-top: 24px;
  padding: 20px;
  background: white;
  border: 2px solid var(--border);
  border-radius: 10px;
}

.data-preview h4 {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-table-wrapper {
  max-height: 250px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.preview-table thead {
  position: sticky;
  top: 0;
  background: var(--bg-gray);
  z-index: 1;
}

.preview-table th {
  padding: 10px 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--text-secondary);
  border-bottom: 2px solid var(--border);
  white-space: nowrap;
}

.preview-table td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  color: var(--text-primary);
}

.preview-table tbody tr:hover {
  background: #f8fafc;
}

.preview-table .code-cell {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: var(--primary);
}

/* Column Mapping Help */
.column-mapping-help {
  margin-top: 16px;
  padding: 16px;
  background: #eff6ff;
  border-left: 4px solid var(--primary);
  border-radius: 8px;
}

.column-mapping-help p {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: var(--text-primary);
}

.column-mapping-help ul {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}

.column-mapping-help li {
  font-size: 13px;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 6px;
}

.column-mapping-help strong {
  color: var(--text-primary);
  font-weight: 600;
}
</style>
